import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Pagination,
  TextInput,
  Select,
  Spinner,
  Dropdown,
} from "flowbite-react";
import {
  HiCheckCircle,
  HiClock,
  HiDotsVertical,
  HiEye,
  HiFilter,
  HiSearch,
  HiTrendingUp,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { Store } from "../context/store";
import apiRequest from "../utils/apiRequest";
import DashboardLayout from "../components/layout/DashboardLayout";

export default function Commission() {
  const store = useContext(Store);
  const [baseUrl] = store.url;
  const navigate = useNavigate();

  const [commissionRequests, setCommissionRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "verified", "pending", "staff", "marketer" etc.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Fetch Commission Requests with pagination, search & filter
  const fetchCommissionRequests = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("royal-token");

        // Compose query params
        let url = new URL(`${baseUrl}/commissions/admin`);
        url.searchParams.append("page", page);

        if (searchTerm) url.searchParams.append("search", searchTerm);
        if (filterStatus) url.searchParams.append("filter", filterStatus);

        const { data: response } = await apiRequest(
          url.toString(),
          "GET",
          null,
          token
        );

        setCommissionRequests(response.data || []);
        setPagination((prev) => ({
          ...prev,
          page: response.pagination?.page,
          totalPages: response.pagination?.totalPages,
        }));
      } catch (error) {
        console.error("Failed to fetch commission requests:", error.message);
      } finally {
        setLoading(false);
      }
    },
    [baseUrl, searchTerm, filterStatus]
  );

  useEffect(() => {
    fetchCommissionRequests(1);
  }, [fetchCommissionRequests]);

  // Handle page change
  const onPageChange = (newPage) => {
    fetchCommissionRequests(newPage);
  };

  const applyFilters = () => {
    fetchCommissionRequests(1);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterStatus("");
  };

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-NG", {
      style: "currency",
      currency: "NGN",
      maximumFractionDigits: 0,
    });

  const analytics = useMemo(() => {
    const totalRequests = commissionRequests.length;
    const approvedRequests = commissionRequests.filter(
      (req) => req.status === "approved",
    ).length;
    const pendingRequests = commissionRequests.filter(
      (req) => req.status === "pending",
    ).length;
    const visiblePropertyValue = commissionRequests.reduce(
      (sum, req) => sum + Number(req.propertyPrice || 0),
      0,
    );

    return {
      totalRequests,
      approvedRequests,
      pendingRequests,
      visiblePropertyValue,
    };
  }, [commissionRequests]);

  const AnalyticsCard = ({
    title,
    value,
    colorClass,
    icon: Icon,
    currency = false,
  }) => (
    <Card className={`shadow-lg p-2 dark:bg-gray-800 border-l-4 ${colorClass}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            {title}
          </p>
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">
            {currency ? formatCurrency(value) : value}
          </h2>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </Card>
  );

  return (
    <DashboardLayout active="commissions" contentClassName="lg:p-8">
      <h5 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6 flex items-center">
        <HiTrendingUp className="w-8 h-8 mr-3 text-emerald-600" />
        Commission Management
      </h5>

      <hr className="my-6 border-gray-200 dark:border-gray-700" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <AnalyticsCard
          title="Visible Requests"
          value={analytics.totalRequests}
          colorClass="text-indigo-500 border-indigo-500"
          icon={HiFilter}
        />
        <AnalyticsCard
          title="Approved"
          value={analytics.approvedRequests}
          colorClass="text-green-500 border-green-500"
          icon={HiCheckCircle}
        />
        <AnalyticsCard
          title="Pending"
          value={analytics.pendingRequests}
          colorClass="text-amber-500 border-amber-500"
          icon={HiClock}
        />
        <AnalyticsCard
          title="Visible Property Value"
          value={analytics.visiblePropertyValue}
          colorClass="text-cyan-500 border-cyan-500"
          icon={HiTrendingUp}
          currency
        />
      </div>

      <div className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            color="light"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="w-full sm:w-auto"
          >
            {filtersOpen ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        {filtersOpen && (
          <div className="mt-4 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col gap-1">
              <h6 className="text-lg font-semibold text-gray-900 dark:text-white">
                Filters
              </h6>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Search commission requests and filter by request or marketer
                status.
              </p>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="lg:col-span-3">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                  Search
                </label>
                <TextInput
                  placeholder="Search by marketer name or phone"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  type="search"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                  Status
                </label>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                  <option value="staff">Staff</option>
                  <option value="marketer">Marketer</option>
                </Select>
              </div>

              <div className="flex flex-col sm:flex-row lg:col-span-2 gap-2 lg:items-end">
                <Button
                  color="dark"
                  onClick={applyFilters}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  <HiSearch className="w-5 h-5 mr-2" />
                  Search
                </Button>
                <Button
                  color="light"
                  onClick={resetFilters}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Card className="shadow-2xl dark:bg-gray-800 overflow-x-auto min-h-[400px]">
        {loading ? (
          <div className="flex justify-center items-center min-h-[320px]">
            <Spinner aria-label="Loading commission requests" size="xl" />
          </div>
        ) : commissionRequests?.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
            <HiTrendingUp className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <h6 className="text-lg font-semibold text-gray-900 dark:text-white">
              No commission requests found
            </h6>
            <p className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
              Adjust your search filters or wait for new commission requests to
              be submitted.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[58vh] scrollbar-always-visible">
            <Table hoverable striped>
              <Table.Head className="sticky top-0 z-10">
                <Table.HeadCell>Marketer</Table.HeadCell>
                <Table.HeadCell>Phone</Table.HeadCell>
                <Table.HeadCell>Property Price</Table.HeadCell>
                <Table.HeadCell>Status</Table.HeadCell>
                <Table.HeadCell>Requested At</Table.HeadCell>
                <Table.HeadCell>
                  <span className="sr-only">Actions</span>
                </Table.HeadCell>
              </Table.Head>

              <Table.Body className="divide-y">
                {commissionRequests.map((req) => (
                  <Table.Row
                    key={req._id}
                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <Table.Cell className="whitespace-nowrap font-semibold text-gray-900 dark:text-white">
                      {req.marketerId
                        ? `${req.marketerId.firstName} ${req.marketerId.lastName}`
                        : "N/A"}
                    </Table.Cell>
                    <Table.Cell>{req.marketerId?.phone || "N/A"}</Table.Cell>
                    <Table.Cell>{formatCurrency(req.propertyPrice)}</Table.Cell>
                    <Table.Cell>
                      <Badge
                        color={
                          req.status === "approved"
                            ? "success"
                            : req.status === "pending"
                              ? "warning"
                              : "gray"
                        }
                        className="capitalize"
                      >
                        {req.status || "Unknown"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(req.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </Table.Cell>

                    <Table.Cell>
                      <Dropdown
                        label={<HiDotsVertical className="w-5 h-5" />}
                        inline
                        arrowIcon={false}
                      >
                        <Dropdown.Item
                          icon={HiEye}
                          onClick={() => navigate(`/commission/${req._id}`)}
                        >
                          View Request
                        </Dropdown.Item>
                      </Dropdown>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>

      <div className="flex overflow-x-auto sm:justify-center mt-6 mb-2">
        <Pagination
          currentPage={pagination?.page}
          totalPages={pagination?.totalPages}
          onPageChange={onPageChange}
          showIcons
        />
      </div>
    </DashboardLayout>
  );
}
